import styled from "@emotion/styled"
import { pdf } from "@react-pdf/renderer"
import { PropsWithChildren, useEffect, useMemo, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { Report } from "../components/pdf/Report"
import { Button } from "../components/Button"
import { Indicator } from "../components/Indicator"
import { Intro } from "../components/Intro"
import { Question } from "../components/Question"
import { Title } from "../components/Title"
import {
  triggerBackEvent,
  triggerCompletedEvent,
  triggerDownloadEvent,
  triggerLessQuestionsEvent,
  triggerMoreQuestionsEvent,
  triggerNextEvent,
  triggerResultBackEvent,
  triggerRetryEvent,
  triggerStartEvent,
} from "../config/analytics"
import {
  calculateRelativeScore,
  getFirstIndexOfNonSkippableQuestion,
  getNextQuestionIndex,
  getPrevQuestionIndex,
  isAnswered,
  QuestionData,
  RadioAnswerData,
  summarizeQuestions,
  useInitialQuestions,
} from "../config/questions"
import { breakPoints, colors } from "../config/theme"
import useSetLanguage from "../hooks/useSetLanguage"
import { SubscribePopup } from "../components/SubscribePopup"
import { PdfIcon } from "../components/PdfIcon"
import useNewsletterUrl from "../hooks/useNewsletterUrl"

const PDF_NAME = "EnergyEfficiencyReport.pdf"

const Base = styled.div`
  flex-direction: column;
  gap: 76px;

  -webkit-font-smoothing: antialiased;
  * {
    box-sizing: border-box;
  }
  // NOTE disable magnolia styles:
  p:first-of-type {
    margin: 0;
  }

  *:focus-visible {
    outline: 2px solid ${colors.black};
    border-radius: 2px;
  }
`

const GreyBox = styled.div`
  min-height: 515px;
  background-color: ${colors.grey};
  padding: 18px;
  overflow: auto;

  @media only screen and (min-width: ${breakPoints.tablet}) {
    padding: 24px;
  }
`

const Controls = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 24px;
  padding: 24px 0;

  @media only screen and (max-width: ${breakPoints.tablet}) {
    width: 100%;
    justify-content: flex-start;
  }
`

const IndicatorWrapper = styled.div`
  width: 108px;
  margin-bottom: 16px;
  margin-top: 16px;
`

const QuestionNumber = styled.div`
  font-family: "Arial";
  font-style: normal;
  font-weight: 700;
  font-size: 13px;
  line-height: 18px;
  letter-spacing: 0.021em;
`

const BigTitle = styled(Title)`
  &::before {
    content: "";
    display: block;
    height: 4px;
    width: 108px;
    background-color: ${colors.primary};
    margin-bottom: 32px;
    margin-top: 32px;
  }
`

const ResultWrapper = styled.div`
  background-color: ${colors.white};
  margin-top: 20px;
  margin-bottom: 25px;
  padding: 0;

  @media only screen and (min-width: ${breakPoints.tablet}) {
    padding: 0 24px;
  }
`

const CtaWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;

  @media only screen and (max-width: ${breakPoints.tablet}) {
    flex-direction: column;
    gap: 10px;
  }
`

const DownloadButton = styled(Button)`
  background-color: ${colors.blue};
  color: ${colors.white};
`

const NewsletterLink = styled.a`
  font-size: 18px;
  line-height: 20px;
  text-align: right;

  @media only screen and (max-width: ${breakPoints.tablet}) {
    text-align: left;
  }
`

const SkipButton = styled(Button)`
  color: ${colors.black};
`

interface QuizProps extends PropsWithChildren {
  language: string
}

export const Quiz: React.FC<QuizProps> = ({ language }) => {
  const { t } = useTranslation()
  const newsletterUrl = useNewsletterUrl(window.location)
  const initialQuestions = useInitialQuestions()
  const [questions, setQuestions] = useState(initialQuestions)
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isDone, setIsDone] = useState(false)
  const [hasStarted, setHasStarted] = useState(false)
  const [hasCompleted, setHasCompleted] = useState(false)
  const [showSubscribe, setShowSubscribe] = useState(false)
  const [subscribed, setSubscribed] = useState(false)

  useEffect(() => {
    setQuestions(initialQuestions)
  }, [initialQuestions])

  useEffect(() => {
    if (hasCompleted) {
      triggerCompletedEvent()
    }
  }, [hasCompleted])

  useSetLanguage(language)

  const downloadPdf = () => {
    if (subscribed) {
      generatePdf(questions)
    } else {
      setShowSubscribe(true)
    }
  }

  const onCloseSubscribe = () => {
    setShowSubscribe(false)
  }

  const onSubmitSubscribe = () => {
    setSubscribed(true)
    generatePdf(questions)
  }

  const generatePdf = (questions: QuestionData[]) => {
    triggerDownloadEvent(PDF_NAME)
    pdf(
      <Report
        {...({
          questions,
          score: relativeScore,
          frontTitle: t("document.title"),
          summaryTitle: t("document.checkOutSummary"),
          summaryText: t("document.adviseInDetail"),
          summaryFooter: t("document.contactPartner"),
          indicatorText: t("summary.yourIndicator"),
          poorText: t("result.poor"),
          goodText: t("result.good"),
        } as any)}
      />,
    )
      .toBlob()
      .then((blob) => {
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement("a")
        a.href = url
        a.download = PDF_NAME
        document.body.appendChild(a)
        a.click()
        a.remove()
      })
  }

  const resetQuestions = () => {
    setQuestions(initialQuestions)
    setCurrentIndex(0)
    setIsDone(false)
    setHasCompleted(false)
    triggerRetryEvent()
  }

  const moveToPreviousQuestion = () => {
    if (isDone) {
      setIsDone(false)
      triggerResultBackEvent()
      return
    }
    if (currentIndex === null) return
    const possiblePrevIndex = getPrevQuestionIndex(currentIndex, questions)
    if (possiblePrevIndex === null) return
    setCurrentIndex(possiblePrevIndex)
    triggerBackEvent(questions[currentIndex].analyticsStep)
  }

  const moveToNextQuestion = ({
    isSkippingQuestion,
  }: {
    isSkippingQuestion: boolean
  }) => {
    if (currentIndex === null) return
    if (!canGoNext && !isSkippingQuestion) return

    triggerNextEvent(questions[currentIndex].analyticsStep)

    const possibleNextIndex = isSkippingQuestion
      ? getFirstIndexOfNonSkippableQuestion(currentIndex, questions)
      : getNextQuestionIndex(currentIndex, questions)
    if (possibleNextIndex === null) {
      setIsDone(true)
      setHasCompleted(true)
      return
    }

    // Analytics for first question
    // to track if extended quiz will be
    // shown or not
    if (currentIndex === 0) {
      const answer = (
        questions[currentIndex].answer as RadioAnswerData
      ).options.find((o) => o.selected)
      if (answer && answer.points === 5) {
        triggerMoreQuestionsEvent()
      } else {
        triggerLessQuestionsEvent()
      }
    }

    setCurrentIndex(possibleNextIndex)
  }

  const questionsAndAnswers = useMemo(
    () => summarizeQuestions(questions),
    [questions],
  )

  const relativeScore = calculateRelativeScore(questions)
  const question = questions[currentIndex]
  const canGoNext = isAnswered(question.answer, questions)
  const shouldRenderSkipButton = Boolean(question.answer.canBeSkipped)

  const resultText = t(
    relativeScore < 25
      ? "summary.resultVeryLow"
      : relativeScore < 50
      ? "summary.resultLow"
      : relativeScore < 75
      ? "summary.resultHigh"
      : "summary.resultVeryHigh",
  )

  if (!hasStarted)
    return (
      <Intro
        onStart={() => {
          triggerStartEvent()
          setHasStarted(true)
        }}
      />
    )

  if (isDone) {
    return (
      <Base>
        <GreyBox>
          <BigTitle>{resultText}</BigTitle>
          <ResultWrapper>
            <Indicator percent={relativeScore} />
          </ResultWrapper>
          <CtaWrapper>
            <DownloadButton onClick={() => downloadPdf()}>
              <PdfIcon />
              <span>{t("summary.download")}</span>
            </DownloadButton>
            {newsletterUrl && (
              <NewsletterLink
                href={newsletterUrl}
                target="_blank"
                rel="noreferrer"
              >
                <Trans i18nKey="summary.signupNewsletter">
                  Wanna stay up to date?
                  <br />
                  Subscribe! `
                </Trans>
              </NewsletterLink>
            )}
          </CtaWrapper>
        </GreyBox>

        <Controls>
          {!(currentIndex === 0) ? (
            <Button variant="secondary" onClick={moveToPreviousQuestion}>
              {t("general.back")}
            </Button>
          ) : null}
          <Button onClick={resetQuestions}>{t("general.retry")}</Button>
        </Controls>

        <SubscribePopup
          isOpen={showSubscribe}
          onClose={onCloseSubscribe}
          onFormSubmit={onSubmitSubscribe}
          questionsAndAnswers={questionsAndAnswers}
        />
      </Base>
    )
  }

  return (
    <Base>
      <GreyBox>
        <QuestionNumber>
          {currentIndex > 0
            ? `${t(`general.question`)} ${question.number} / ${t(
                (questions[0].answer as RadioAnswerData).options[0].selected
                  ? "question16.nr"
                  : "question9.nr",
              )}`
            : ""}
          <br />
        </QuestionNumber>

        <IndicatorWrapper>
          <Indicator percent={relativeScore} brief />
        </IndicatorWrapper>

        <Question
          onChangeAnswer={(newAnswer) => {
            const newQuestions = [...questions]
            newQuestions[currentIndex].answer = newAnswer
            setQuestions(newQuestions)
          }}
          question={question}
        />
      </GreyBox>
      <Controls>
        {shouldRenderSkipButton ? (
          <SkipButton
            variant="secondary"
            onClick={() => moveToNextQuestion({ isSkippingQuestion: true })}
          >
            {t("general.skip")}
          </SkipButton>
        ) : null}
        {!(currentIndex === 0) ? (
          <Button
            variant="secondary"
            onClick={moveToPreviousQuestion}
            tabIndex={-1}
          >
            {t("general.back")}
          </Button>
        ) : null}
        <Button
          onClick={() => moveToNextQuestion({ isSkippingQuestion: false })}
          disabled={!canGoNext}
        >
          {t("general.next")}
        </Button>
      </Controls>
    </Base>
  )
}
